import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { IDocumentSettingSliceState } from "../../utils/documentSetting.types";
import { ApiStatus } from "../../utils/api.types";
import { addDocumentSetting, deleteDocumentSetting, fetchDocumentSettings, updateDocumentSetting, fetchDocumentSettingById } from "./documentSettingThunk";

/**
 * DocumentSetting slice initial state.
 */
const initialState: IDocumentSettingSliceState = {
    documentSettings: [],
    documentSettingDetails: undefined,
    errorMessage: '',
    isDocumentSettingCreated: false,
    selectedDocumentSettingId: '',
    status: ApiStatus.IDLE
}

/**
 * DocumentSetting Slice.
 */
const documentSettingSlice = createSlice({
    name: 'DocumentSetting',
    initialState: initialState,
    reducers: {
        // Selected DocumentSetting id
        selectedDocumentSettingID: (state, action) => {
            state.selectedDocumentSettingId = action.payload || '';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch DocumentSettings
            .addCase(fetchDocumentSettings.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchDocumentSettings.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.documentSettings = action.payload.documentSettings || [];
                state.errorMessage = '';
            })
            .addCase(fetchDocumentSettings.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch DocumentSetting by ID
            .addCase(fetchDocumentSettingById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchDocumentSettingById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.documentSettingDetails = action.payload || undefined;
                state.errorMessage = '';
            })
            .addCase(fetchDocumentSettingById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Add DocumentSetting
            .addCase(addDocumentSetting.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addDocumentSetting.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.isDocumentSettingCreated = true;
                state.documentSettings.push(action.payload.documentSetting);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addDocumentSetting.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isDocumentSettingCreated = false;
                state.errorMessage = action.error.message
            })

            // Delete DocumentSetting
            .addCase(deleteDocumentSetting.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteDocumentSetting.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.documentSettings = state.documentSettings.filter(item => item._id !== id)

                toast.success(message);
            })
            .addCase(deleteDocumentSetting.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })

            // Update DocumentSetting
            .addCase(updateDocumentSetting.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateDocumentSetting.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, documentSetting: updatedDocumentSetting } = action.payload
                state.documentSettings = state.documentSettings
                    .map(item => (item._id === item._id ? { ...item, ...updateDocumentSetting } : item));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateDocumentSetting.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
    }
});

export default documentSettingSlice.reducer;
